/* eslint-disable no-param-reassign */
import { AssetTypes } from '@tassat/common-definitions';
import {
    validateDescriptionFields,
    validateNoExcelFormulaStartingCharacters,
    validateNoNonPrintable
} from '@tassat/forms/src/helpers/validationHelpers';
import { AccountBalanceStats } from '@tassat/fred-extended-definitions';
import { stripPrefix } from '@tassat/utility';

import { AccountAddressDetails, CreatePledgeFormAction, CreatePledgeFormState } from './CreatePledgeFormReducer';
import { ICreatePledgeFormValues, IFormErrors } from './types';

export const validateCreatePledge = async (
    values: ICreatePledgeFormValues,
    opts: {
        errors: IFormErrors;
        formState: CreatePledgeFormState;
        dispatchFormAction: React.Dispatch<CreatePledgeFormAction>;
        validateWalletAddressWithCore: (
            values: ICreatePledgeFormValues,
            dispatch: React.Dispatch<CreatePledgeFormAction>
        ) => Promise<string | AccountAddressDetails>;
        accountPositionSecurityBalance?: AccountBalanceStats;
    }
) => {
    const {
        errors,
        formState,
        dispatchFormAction,
        validateWalletAddressWithCore,
        accountPositionSecurityBalance
    } = opts;

    if (!formState.AccountAddressValidated && formState.AccountAddressDetails.AccountName) {
        dispatchFormAction({ type: 'RESET_ACCOUNT_DETAILS' });
    }

    if (!values.AssetType.value){
        errors.AssetType = 'Please select an asset type';
    }

    if (!values.Recipient && !values.RecipientAddress) {
        errors.Recipient = 'Please select a recipient for post';
    }

    if (values.Recipient !== null){
        if (values.Sender.Account === values.Recipient.value.Account){
            errors.Recipient = 'Cannot post funds to your own wallet';
        }
    }

    const publicDescriptionError = validateDescriptionFields(values.PublicDescription, 'Public Description');
    const publicDescriptionExcelError = validateNoExcelFormulaStartingCharacters(values.PublicDescription);
    const publicDescriptionNonPrintableError = validateNoNonPrintable(values.PublicDescription);

    if (publicDescriptionError) {
        errors.PublicDescription = publicDescriptionError;
    } else if (publicDescriptionExcelError) {
        errors.PublicDescription = publicDescriptionExcelError;
    } else if (publicDescriptionNonPrintableError) {
        errors.PublicDescription = publicDescriptionNonPrintableError;
    }

    const privateDescriptionError = validateDescriptionFields(values.PrivateDescription, 'Private Description');
    const privateDescriptionExcelError = validateNoExcelFormulaStartingCharacters(values.PrivateDescription);
    const privateDescriptionNonPrintableError = validateNoNonPrintable(values.PrivateDescription);
    if (privateDescriptionError) {
        errors.PrivateDescription = privateDescriptionError;
    } else if (privateDescriptionExcelError) {
        errors.PrivateDescription = privateDescriptionExcelError;
    } else if (privateDescriptionNonPrintableError) {
        errors.PrivateDescription = privateDescriptionNonPrintableError;
    }

    if (values.RecipientAddress && !formState.AccountAddressValidated){
        try {
            const coreResponse = await validateWalletAddressWithCore(
                values,
                dispatchFormAction
            );
            if (typeof coreResponse === 'string') {
                errors.RecipientAddress = coreResponse;
                dispatchFormAction({
                    type: 'SERVER_ERROR',
                    payload: { error: coreResponse }
                });
            } else if (typeof coreResponse === 'object') {
                dispatchFormAction({
                    type: 'SERVER_SUCCESS',
                    payload: { AccountAddressDetails: coreResponse as AccountAddressDetails }
                });
            }
        } catch (error) {
            errors.RecipientAddress = 'Could not validate recipient address, please try again';
        }
    }

    if (values.AssetType.refData?.security?.Type === AssetTypes.ASSET) {
        const securityValue = Number(stripPrefix(values.AssetAmount, `${accountPositionSecurityBalance?.ticker} `)
            .replace(/,/g, ''));

        if (!securityValue) {
            errors.AssetAmount = 'Please enter an amount';
        }

        if (securityValue > Number(accountPositionSecurityBalance?.availableBalance ?? 0)) {
            errors.AssetAmount = `Amount must not exceed the available ${accountPositionSecurityBalance?.ticker} balance of the account`;
        }
    }

    return errors;
};